'use client';

import { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import dynamic from 'next/dynamic';
import ReactECharts from 'echarts-for-react';
import * as echarts from 'echarts';
import { useTheme } from 'next-themes';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { Footprints, Bike, Mountain, Clock, TrendingUp, TrendingDown, HeartPulse, Gauge, Loader2, RefreshCw } from 'lucide-react';
import type { ParsedFitResult } from '@/lib/fitParser';

// ─── Colours ──────────────────────────────────────────────────────────────────

const P = {
  emerald: '#10b981', teal: '#14b8a6', sky: '#0ea5e9',
  amber: '#f59e0b',   rose: '#f43f5e', slate: '#94a3b8', violet: '#8b5cf6',
};

function lerpHex(a: string, b: string, t: number): string {
  const n = (s: string) => parseInt(s.slice(1), 16);
  const ah = n(a), bh = n(b);
  const ch = (mask: number, sh: number) =>
    Math.round(((ah >> sh) & mask) + (((bh >> sh) & mask) - ((ah >> sh) & mask)) * t)
      .toString(16).padStart(2, '0');
  return `#${ch(0xff,16)}${ch(0xff,8)}${ch(0xff,0)}`;
}
function elevColor(norm: number) {
  return norm < 0.33
    ? lerpHex('#10b981', '#f59e0b', norm / 0.33)
    : lerpHex('#f59e0b', '#f43f5e', (norm - 0.33) / 0.67);
}

// ─── Elevation helpers ────────────────────────────────────────────────────────

/** True when array contains real altitude (not all-zero filler) */
function hasRealAlt(arr: number[]): boolean {
  if (!arr.length) return false;
  const min = Math.min(...arr);
  const max = Math.max(...arr);
  // Sea-level (0m) is valid — we need spread, not just non-zero
  return max - min > 2;
}

/** Resample `src` array to exactly `targetLen` items via linear interpolation */
function resampleTo(src: number[], targetLen: number): number[] {
  if (!src.length || targetLen === 0) return [];
  if (src.length === targetLen) return src;
  return Array.from({ length: targetLen }, (_, i) => {
    const pos = (i / Math.max(1, targetLen - 1)) * (src.length - 1);
    const lo  = Math.floor(pos);
    const hi  = Math.min(lo + 1, src.length - 1);
    return src[lo] + (src[hi] - src[lo]) * (pos - lo);
  });
}

function elevStats(alts: number[]) {
  let gain = 0, loss = 0;
  for (let i = 1; i < alts.length; i++) {
    const d = alts[i] - alts[i - 1];
    if (d > 0.5) gain += d; else if (d < -0.5) loss -= d;
  }
  return { gain: Math.round(gain), loss: Math.round(loss),
           max: Math.round(Math.max(...alts)), min: Math.round(Math.min(...alts)) };
}

/**
 * Fetch elevation from Open-Meteo (free, no API key, no rate limit, excellent CORS).
 * Returns array of same length as input lats/lngs.
 * API: GET https://api.open-meteo.com/v1/elevation?latitude=a,b&longitude=c,d
 * Max 100 locations per call.
 */
async function fetchElevation(lats: number[], lngs: number[]): Promise<number[]> {
  // Filter valid GPS points (non-zero)
  const valid = lats
    .map((lat, i) => ({ lat, lng: lngs[i], i }))
    .filter(p => !(p.lat === 0 && p.lng === 0));

  if (valid.length === 0) return new Array(lats.length).fill(0);

  // Decimate to ≤100 pts for a single API request
  const MAX = 100;
  const step    = Math.max(1, Math.floor(valid.length / MAX));
  const sampled = valid.filter((_, idx) => idx % step === 0).slice(0, MAX);

  const latStr = sampled.map(p => p.lat.toFixed(5)).join(',');
  const lngStr = sampled.map(p => p.lng.toFixed(5)).join(',');

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 15_000);

  let sampledElev: number[];
  try {
    const resp = await fetch(
      `https://api.open-meteo.com/v1/elevation?latitude=${latStr}&longitude=${lngStr}`,
      { signal: controller.signal }
    );
    clearTimeout(timer);
    if (!resp.ok) throw new Error(`Open-Meteo HTTP ${resp.status}`);
    const json = await resp.json();
    sampledElev = json.elevation ?? [];
    if (!sampledElev.length) throw new Error('Empty elevation response');
  } catch (err) {
    clearTimeout(timer);
    throw err;
  }

  // Interpolate sampled elevations back to full valid-point count
  const fullElev = resampleTo(sampledElev, valid.length);

  // Write back into a result array indexed by original position
  const result = new Array(lats.length).fill(0);
  valid.forEach((p, vi) => { result[p.i] = fullElev[vi]; });
  return result;
}

// ─── Sport classifier ─────────────────────────────────────────────────────────
function isRun(sport: string)  { const s = sport.toLowerCase(); return s.includes('run') || s.includes('trail'); }
function isBike(sport: string) { const s = sport.toLowerCase(); return s.includes('cycl') || s.includes('bik'); }
function isMappable(sport: string) { return isRun(sport) || isBike(sport); }

function sportLabel(w: ParsedFitResult) {
  if (isRun(w.sport ?? ''))  return { label: 'Run',  colour: P.emerald, icon: <Footprints className="h-3 w-3" /> };
  if (isBike(w.sport ?? '')) return { label: 'Ride', colour: P.sky,     icon: <Bike        className="h-3 w-3" /> };
  return { label: 'Activity', colour: P.slate, icon: null };
}

// ─── Formatting ───────────────────────────────────────────────────────────────

function fmtPace(s: number) {
  if (!s || !isFinite(s)) return '—';
  return `${Math.floor(s / 60)}:${Math.round(s % 60).toString().padStart(2, '0')} /km`;
}
function fmtSpeed(mps: number) {
  if (!mps || !isFinite(mps)) return '—';
  return `${(mps * 3.6).toFixed(1)} km/h`;
}
function fmtDur(min: number) {
  const h = Math.floor(min / 60), m = Math.round(min % 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

// ─── UI atoms ─────────────────────────────────────────────────────────────────

function StatPill({ icon, label, value, accent }: { icon: React.ReactNode; label: string; value: string; accent: string }) {
  return (
    <div className="flex items-center gap-2.5 px-3 py-3 rounded-2xl bg-card border border-border/50 shadow-[0_2px_12px_rgba(0,0,0,0.06)] dark:shadow-[0_2px_12px_rgba(0,0,0,0.28)]">
      <div className="shrink-0 w-8 h-8 rounded-lg flex items-center justify-center"
           style={{ background: `${accent}22` }}>
        <span style={{ color: accent }}>{icon}</span>
      </div>
      <div className="flex flex-col min-w-0">
        <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">{label}</span>
        <span className="text-sm font-semibold tracking-tight">{value}</span>
      </div>
    </div>
  );
}

function ActivityCard({ w, gain, active, onClick }: {
  w: ParsedFitResult; gain?: number; active: boolean; onClick(): void;
}) {
  const distKm  = (w.totalDistance || 0) / 1000;
  const durMin  = (w.totalDuration || 0) / 60;
  const { label, colour, icon } = sportLabel(w);
  const ride = isBike(w.sport ?? '');
  const speedOrPace = ride
    ? fmtSpeed((w.totalDistance || 0) / (w.totalDuration || 1))
    : fmtPace(distKm > 0 ? ((w.totalDuration ?? 0) / distKm) : 0);
  return (
    <button onClick={onClick}
      className={`flex-shrink-0 flex flex-col text-left gap-1.5 px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl border text-xs w-40 sm:w-48 transition-all duration-150
        ${active ? 'bg-foreground text-background border-foreground'
                 : 'bg-card border-border/60 text-muted-foreground hover:border-foreground/30 hover:text-foreground'}`}>
      <div className="flex items-center justify-between w-full font-medium">
        <span>{w.startTime.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: '2-digit' })}</span>
        <span className="flex items-center gap-1" style={active ? {} : { color: colour }}>{icon}{label}</span>
      </div>
      <span className="text-base font-semibold tracking-tight">
        {distKm.toFixed(1)}<span className="text-xs font-normal ml-0.5">km</span>
      </span>
      <div className="flex items-center gap-2 opacity-80">
        {gain != null && gain > 0 && <span className="flex items-center gap-0.5"><Mountain className="h-2.5 w-2.5" />+{gain}m</span>}
        <span>{fmtDur(durMin)}</span>
      </div>
    </button>
  );
}

// ─── Leaflet map (SSR-safe) ───────────────────────────────────────────────────

interface MapProps {
  lats: number[]; lngs: number[]; alts: number[];
  hoverIdx: number | null; isDark: boolean;
}

function LeafletInner({ lats, lngs, alts, hoverIdx, isDark }: MapProps) {
  const rootRef    = useRef<HTMLDivElement>(null);
  const mapRef     = useRef<any>(null);
  const LRef       = useRef<any>(null);
  const tileRef    = useRef<any>(null);
  const polysRef   = useRef<any[]>([]);
  const hoverDotRef= useRef<any>(null);
  const [mapLoaded, setMapLoaded] = useState(false);

  const elevStats = useMemo(() => {
    if (!alts.length || !lats.length) return null;
    const validAlts = alts.filter((a, i) => !(lats[i] === 0 && lngs[i] === 0));
    if (!validAlts.length) return null;
    const min = Math.min(...validAlts);
    const max = Math.max(...validAlts);
    return { min, max, hasElev: (max - min) > 2 };
  }, [alts, lats, lngs]);

  function tileUrl(dark: boolean) {
    return dark
      ? 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png'
      : 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png';
  }
  const tileOpts = {
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a> &copy; <a href="https://carto.com/">CARTO</a>',
    subdomains: 'abcd', maxZoom: 19,
  };

  // Init once
  useEffect(() => {
    if (typeof window === 'undefined' || !rootRef.current || mapRef.current) return;
    if (typeof document !== 'undefined' && !document.getElementById('leaflet-css')) {
      const l = document.createElement('link');
      l.id = 'leaflet-css'; l.rel = 'stylesheet';
      l.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      document.head.appendChild(l);
    }
    import('leaflet').then(L => {
      if (!rootRef.current || mapRef.current) return;
      delete (L.Icon.Default.prototype as any)._getIconUrl;
      const map = L.map(rootRef.current, { zoomControl: true, scrollWheelZoom: true, center: [0, 0], zoom: 2 });
      tileRef.current = L.tileLayer(tileUrl(isDark), tileOpts).addTo(map);
      mapRef.current = map;
      LRef.current   = L;
      setMapLoaded(true);
      
      const observer = new ResizeObserver(() => map.invalidateSize());
      observer.observe(rootRef.current);
      (map as any)._resizeObserver = observer;
    });
    return () => { 
      if (mapRef.current) { 
        if (mapRef.current._resizeObserver) mapRef.current._resizeObserver.disconnect();
        mapRef.current.remove(); 
        mapRef.current = null; 
      } 
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Swap tiles on theme change
  useEffect(() => {
    if (!mapRef.current || !LRef.current) return;
    if (tileRef.current) mapRef.current.removeLayer(tileRef.current);
    tileRef.current = LRef.current.tileLayer(tileUrl(isDark), tileOpts).addTo(mapRef.current);
  }, [isDark]); // eslint-disable-line react-hooks/exhaustive-deps

  // Draw route
  useEffect(() => {
    if (!mapLoaded || !mapRef.current || !LRef.current || !lats.length) return;
    const map = mapRef.current, L = LRef.current;
    polysRef.current.forEach(p => map.removeLayer(p));
    polysRef.current = [];

    const pts = lats.map((lat, i) => ({ lat, lng: lngs[i], alt: alts[i] ?? 0 }))
                    .filter(p => !(p.lat === 0 && p.lng === 0));
    if (!pts.length) return;

    const altMin = Math.min(...pts.map(p => p.alt));
    const altMax = Math.max(...pts.map(p => p.alt));
    const altRange = altMax - altMin;
    const hasElev = altRange > 2;

    const seg = Math.max(1, Math.floor(pts.length / 200));
    for (let i = 0; i < pts.length - seg; i += seg) {
      const slice = pts.slice(i, i + seg + 1);
      const mid   = (slice[0].alt + slice[slice.length - 1].alt) / 2;
      const color = hasElev ? elevColor((mid - altMin) / altRange) : P.emerald;
      polysRef.current.push(
        L.polyline(slice.map(p => [p.lat, p.lng] as [number, number]),
          { color, weight: 5, opacity: 0.95, lineCap: 'round', lineJoin: 'round' }
        ).addTo(map)
      );
    }

    const dot = (c: string) => L.divIcon({
      html: `<div style="width:12px;height:12px;background:${c};border:2.5px solid white;border-radius:50%;box-shadow:0 1px 6px rgba(0,0,0,.5)"></div>`,
      className: '', iconAnchor: [6, 6],
    });
    L.marker([pts[0].lat, pts[0].lng],               { icon: dot('#10b981') }).addTo(map);
    L.marker([pts[pts.length-1].lat, pts[pts.length-1].lng], { icon: dot('#f43f5e') }).addTo(map);
    
    setTimeout(() => {
      try {
        if (!mapRef.current) return;
        map.invalidateSize();
        map.fitBounds(L.latLngBounds(pts.map(p => [p.lat, p.lng] as [number, number])), { padding: [32, 32], maxZoom: 16 });
      } catch (e) { console.error('Map fit bounds error:', e); }
    }, 100);

    setTimeout(() => { if (mapRef.current) mapRef.current.invalidateSize(); }, 600);
    setTimeout(() => { if (mapRef.current) mapRef.current.invalidateSize(); }, 1500);
  }, [lats, lngs, alts, mapLoaded]);

  // Hover crosshair
  useEffect(() => {
    if (!mapRef.current || !LRef.current) return;
    if (hoverDotRef.current) { mapRef.current.removeLayer(hoverDotRef.current); hoverDotRef.current = null; }
    if (hoverIdx == null || !lats[hoverIdx] || (lats[hoverIdx] === 0 && lngs[hoverIdx] === 0)) return;
    hoverDotRef.current = LRef.current.marker([lats[hoverIdx], lngs[hoverIdx]], {
      icon: LRef.current.divIcon({
        html: `<div style="width:10px;height:10px;background:white;border:2px solid #0ea5e9;border-radius:50%;box-shadow:0 0 8px rgba(14,165,233,.8)"></div>`,
        className: '', iconAnchor: [5, 5],
      }),
    }).addTo(mapRef.current);
  }, [hoverIdx, lats, lngs]);

  return (
    <div className="w-full h-full relative group">
      <div ref={rootRef} className="absolute inset-0 z-0" />
      {elevStats?.hasElev && (
        <div className="absolute bottom-4 left-4 z-[400] bg-background/90 backdrop-blur-md px-3 py-2.5 rounded-xl border border-border shadow-lg flex flex-col gap-2 pointer-events-none transition-opacity duration-300">
          <span className="text-[9px] font-bold tracking-widest uppercase text-muted-foreground flex items-center gap-1.5">
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m8 3 4 8 5-5 5 15H2L8 3z"/></svg>
            Elevation
          </span>
          <div className="flex flex-col gap-1 w-32">
            <div className="h-1.5 w-full rounded-full bg-gradient-to-r from-emerald-500 via-amber-500 to-rose-500 shadow-inner"></div>
            <div className="flex items-center justify-between text-[10px] font-semibold text-foreground/80">
              <span>{Math.round(elevStats.min)}m</span>
              <span>{Math.round(elevStats.max)}m</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export const MapPanel = dynamic(() => Promise.resolve(LeafletInner), { ssr: false });

// ─── Main component ───────────────────────────────────────────────────────────

type SportFilter = 'all' | 'run' | 'ride';
type ElevCacheEntry = { alts: number[]; stats: ReturnType<typeof elevStats> };

export function TrailRunMapView({ realData }: { realData?: ParsedFitResult[] | null }) {
  const { resolvedTheme } = useTheme();
  const [mounted,       setMounted]       = useState(false);
  const [selectedIdx,   setSelectedIdx]   = useState(0);
  const [sportFilter,   setSportFilter]   = useState<SportFilter>('all');
  const [hoverIdx,      setHoverIdx]      = useState<number | null>(null);
  const [cache,         setCache]         = useState<Record<string, ElevCacheEntry>>({});
  const [fetching,      setFetching]      = useState<string | null>(null);
  const [fetchErr,      setFetchErr]      = useState<string | null>(null);

  useEffect(() => {
    if (typeof document !== 'undefined' && !document.getElementById('leaflet-css-main')) {
      // Intentionally left blank as LeafletInner now handles it natively, but keep ID check to prevent breaking existing hooks
    }
    setMounted(true);
  }, []);

  const isDark = mounted ? resolvedTheme === 'dark' : true;
  const textC  = isDark ? '#94a3b8' : '#6b7280';
  const gridC  = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)';
  const ttBase = { backgroundColor: isDark ? '#1c1c1e' : '#fff', borderColor: 'transparent',
                   textStyle: { color: isDark ? '#f0f0f0' : '#1a1a1a', fontSize: 11 } };

  // ── All mappable activities (runs + rides) filtered by sport picker ──────────
  const allActivities = useMemo(() => {
    if (!realData) return [];
    return realData
      .filter(w => isMappable(w.sport ?? ''))
      .sort((a, b) => b.startTime.getTime() - a.startTime.getTime());
  }, [realData]);

  const activities = useMemo(() => {
    if (sportFilter === 'run')  return allActivities.filter(w => isRun(w.sport ?? ''));
    if (sportFilter === 'ride') return allActivities.filter(w => isBike(w.sport ?? ''));
    return allActivities;
  }, [allActivities, sportFilter]);

  // Keep selectedIdx in bounds when filter changes
  const clampedIdx = Math.min(selectedIdx, Math.max(0, activities.length - 1));
  const sel = activities[clampedIdx] ?? null;
  const selIsBike = isBike(sel?.sport ?? '');

  // ── Decimate GPS → ≤500 pts + build distance axis ─────────────────────────
  const gps = useMemo(() => {
    if (!sel) return { lats: [], lngs: [], rawLats: [], rawLngs: [], dists: [], n: 0 };
    const rawLats = sel.telemetry.gps_lat;
    const rawLngs = sel.telemetry.gps_lng;
    const n = rawLats.length;
    if (!n) return { lats: [], lngs: [], rawLats, rawLngs, dists: [], n };

    const step = Math.max(1, Math.floor(n / 500));
    const lats: number[] = [], lngs: number[] = [], dists: number[] = [];
    let cum = 0;
    for (let i = 0; i < n; i += step) {
      lats.push(rawLats[i]); lngs.push(rawLngs[i]);
      if (i > 0) {
        const p = i - step;
        const dLat = (rawLats[i] - rawLats[p]) * 111_000;
        const dLng = (rawLngs[i] - rawLngs[p]) * 111_000 * Math.cos(rawLats[i] * Math.PI / 180);
        cum += Math.sqrt(dLat * dLat + dLng * dLng);
      }
      dists.push(+(cum / 1000).toFixed(3));
    }
    return { lats, lngs, rawLats, rawLngs, dists, n };
  }, [sel]);

  const hasGps = gps.lats.some(v => v !== 0);

  // ── Resolve altitude (priority: stored → cached API) ────────────────────
  // Always resample to match gps.lats.length so axes align perfectly
  const { alts, stats: altStats } = useMemo(() => {
    const target = gps.lats.length;
    if (!target) return { alts: [] as number[], stats: null };

    const key = sel?.startTime.toISOString() ?? '';

    // 1. Stored altitude from FIT file
    const stored = sel?.telemetry.altitude ?? [];
    if (hasRealAlt(stored)) {
      const a = resampleTo(stored, target);
      return { alts: a, stats: elevStats(a) };
    }

    // 2. API-fetched altitude in cache
    const cached = cache[key];
    if (cached) return { alts: cached.alts, stats: cached.stats };

    return { alts: [] as number[], stats: null };
  }, [sel, gps.lats.length, cache]);

  // ── HR resampled to same length as GPS ────────────────────────────────────
  const hrAlts = useMemo(() => {
    if (!sel) return [];
    const hr = sel.telemetry.heart_rate;
    if (!hr.length) return [];
    return resampleTo(hr, gps.lats.length);
  }, [sel, gps.lats.length]);

  // ── Auto-fetch elevation when GPS available but no altitude ──────────────
  const selKey = sel?.startTime.toISOString() ?? '';

  useEffect(() => {
    if (!sel || !hasGps) return;
    if (hasRealAlt(sel.telemetry.altitude)) return;   // have stored altitude
    if (cache[selKey]) return;                          // already cached
    if (fetching === selKey) return;                   // in flight
    if (!gps.rawLats.length) return;

    console.log('[Maps] Fetching elevation from Open-Meteo for', selKey);
    setFetching(selKey);
    setFetchErr(null);

    fetchElevation(gps.rawLats, gps.rawLngs)
      .then(raw => {
        // Resample once to target length and compute stats
        const resampled = resampleTo(raw, gps.lats.length);
        console.log('[Maps] Elevation fetched, range:', Math.min(...resampled).toFixed(0), '→', Math.max(...resampled).toFixed(0), 'm');
        setCache(prev => ({ ...prev, [selKey]: { alts: resampled, stats: elevStats(resampled) } }));
      })
      .catch(err => {
        console.error('[Maps] Elevation fetch error:', err);
        setFetchErr(`Elevation fetch failed: ${err.message ?? err}`);
      })
      .finally(() => setFetching(null));

  }, [selKey, hasGps, sel, gps.rawLats, gps.rawLngs, gps.lats.length, cache, fetching]);

  // ── ECharts colour — changes per sport ────────────────────────────────────
  const lineColour = selIsBike ? P.sky : P.emerald;

  // ── ECharts options ───────────────────────────────────────────────────────
  const elevOpt = useMemo(() => {
    if (!alts.length || !hasRealAlt(alts)) return null;
    return {
      backgroundColor: 'transparent',
      textStyle: { color: textC, fontFamily: 'Inter,sans-serif', fontSize: 11 },
      tooltip: {
        trigger: 'axis', ...ttBase,
        formatter: (p: any) => `<b>${gps.dists[p[0].dataIndex]?.toFixed(2) ?? '?'} km</b><br/>Elevation: <b>${Math.round(p[0].value)} m</b>`,
        axisPointer: { type: 'line', lineStyle: { color: P.sky, width: 1, type: 'dashed' } },
      },
      grid: { left: 0, right: 0, bottom: 28, top: 16, containLabel: true },
      xAxis: {
        type: 'category', data: gps.dists.map(d => d.toFixed(1)), boundaryGap: false,
        axisLine: { lineStyle: { color: gridC } }, axisTick: { show: false },
        axisLabel: { color: textC, fontSize: 10, interval: Math.max(0, Math.floor(gps.dists.length / 8) - 1), formatter: (v: string) => `${v}km` },
      },
      yAxis: {
        type: 'value', name: 'm', axisLine: { show: false }, axisTick: { show: false },
        min: (v: any) => Math.max(0, v.min - 20),
        axisLabel: { color: textC, fontSize: 10 },
        splitLine: { lineStyle: { color: gridC, type: 'dashed' } },
      },
      series: [{
        type: 'line', data: alts, showSymbol: false, smooth: 0.3,
        lineStyle: { color: lineColour, width: 2 }, itemStyle: { color: lineColour },
        areaStyle: { color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
          { offset: 0, color: `${lineColour}55` }, { offset: 0.7, color: `${P.amber}18` }, { offset: 1, color: `${lineColour}00` },
        ])},
        markLine: { silent: true, lineStyle: { color: P.amber, width: 1, type: 'dashed', opacity: 0.5 },
          data: [{ type: 'average', label: { formatter: 'avg {c}m', color: P.amber, fontSize: 10 } }] },
      }],
    };
  }, [alts, gps.dists, isDark, lineColour]);

  const hrOpt = useMemo(() => {
    if (!hrAlts.length || !gps.dists.length) return null;
    const len = Math.min(hrAlts.length, gps.dists.length);
    return {
      backgroundColor: 'transparent',
      textStyle: { color: textC, fontFamily: 'Inter,sans-serif', fontSize: 11 },
      tooltip: {
        trigger: 'axis', ...ttBase,
        formatter: (p: any) => `<b>${gps.dists[p[0].dataIndex]?.toFixed(2)} km</b><br/>HR: <b>${Math.round(p[0].value)} bpm</b>`,
        axisPointer: { type: 'line', lineStyle: { color: P.rose, width: 1, type: 'dashed' } },
      },
      grid: { left: 0, right: 0, bottom: 28, top: 16, containLabel: true },
      xAxis: {
        type: 'category', data: gps.dists.slice(0, len).map(d => d.toFixed(1)), boundaryGap: false,
        axisLine: { lineStyle: { color: gridC } }, axisTick: { show: false },
        axisLabel: { color: textC, fontSize: 10, interval: Math.max(0, Math.floor(len / 8) - 1), formatter: (v: string) => `${v}km` },
      },
      yAxis: {
        type: 'value', name: 'bpm', min: (v: any) => Math.max(60, v.min - 10),
        axisLine: { show: false }, axisTick: { show: false },
        axisLabel: { color: textC, fontSize: 10 },
        splitLine: { lineStyle: { color: gridC, type: 'dashed' } },
      },
      series: [{
        type: 'line', data: hrAlts.slice(0, len), showSymbol: false, smooth: 0.4,
        lineStyle: { color: P.rose, width: 2 }, itemStyle: { color: P.rose },
        areaStyle: { color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
          { offset: 0, color: `${P.rose}44` }, { offset: 1, color: `${P.rose}00` },
        ])},
      }],
    };
  }, [hrAlts, gps.dists, isDark]);

  const onElevHover = useCallback((p: any) => {
    setHoverIdx(p?.dataIndex ?? null);
  }, []);

  const isFetching = fetching === selKey;
  const distKm  = (sel?.totalDistance || 0) / 1000;
  const durMin  = (sel?.totalDuration || 0) / 60;
  const avgPace = distKm > 0 ? ((sel?.totalDuration ?? 0) / distKm) : 0;
  const avgSpeedMps = (sel?.totalDistance || 0) / (sel?.totalDuration || 1);

  // ── Retry fetch ───────────────────────────────────────────────────────────
  function retry() {
    if (!selKey) return;
    setCache(prev => { const n = { ...prev }; delete n[selKey]; return n; });
    setFetchErr(null);
  }

  if (!mounted) return null;

  const runCount  = allActivities.filter(w => isRun(w.sport ?? '')).length;
  const rideCount = allActivities.filter(w => isBike(w.sport ?? '')).length;

  if (!allActivities.length) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-4 text-center">
        <div className="w-14 h-14 rounded-2xl bg-muted flex items-center justify-center">
          <Mountain className="w-7 h-7 text-muted-foreground" />
        </div>
        <p className="text-sm font-medium">No GPS activities found</p>
        <p className="text-xs text-muted-foreground max-w-xs">
          Sync your Garmin data via the cloud icon or upload a .fit file. Running and cycling activities with GPS are shown here.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">

      {/* Header */}
      <div>
        <h2 className="text-base font-semibold tracking-tight">Activity Maps</h2>
        <p className="text-sm text-muted-foreground mt-0.5">
          GPS routes with elevation profiles — running and cycling. Elevation sourced from Garmin FIT data or SRTM terrain.
        </p>
      </div>

      {/* Sport filter tabs */}
      <div className="flex items-center gap-0.5 bg-muted/50 rounded-lg p-1 w-fit">
        {(['all', 'run', 'ride'] as SportFilter[]).map(f => (
          <button
            key={f}
            onClick={() => { setSportFilter(f); setSelectedIdx(0); setFetchErr(null); }}
            className={`px-3 py-1 rounded-md text-xs font-medium transition-all duration-150 ${
              sportFilter === f ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {f === 'all'  ? `All (${allActivities.length})`  :
             f === 'run'  ? `Runs (${runCount})`  :
             `Rides (${rideCount})`}
          </button>
        ))}
      </div>

      {/* Activity picker */}
      <ScrollArea className="w-full">
        <div className="flex gap-2 pb-2">
          {activities.map((w, i) => (
            <ActivityCard
              key={w.startTime.toISOString()}
              w={w}
              gain={(() => {
                const key = w.startTime.toISOString();
                if (cache[key]) return cache[key].stats.gain;
                if (hasRealAlt(w.telemetry.altitude)) return elevStats(w.telemetry.altitude).gain;
                return undefined;
              })()}
              active={clampedIdx === i}
              onClick={() => { setSelectedIdx(i); setFetchErr(null); }}
            />
          ))}
        </div>
        <ScrollBar orientation="horizontal" className="opacity-40" />
      </ScrollArea>

      {sel && (
        <>
          {/* Stats */}
          <div className={`grid gap-2 ${altStats ? 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7' : 'grid-cols-2 md:grid-cols-4'}`}>
            {selIsBike ? (
              <StatPill icon={<Bike       className="w-4 h-4"/>} label="Distance" value={`${distKm.toFixed(2)} km`} accent={P.sky}/>
            ) : (
              <StatPill icon={<Footprints className="w-4 h-4"/>} label="Distance" value={`${distKm.toFixed(2)} km`} accent={P.emerald}/>
            )}
            <StatPill icon={<Clock      className="w-4 h-4"/>} label="Duration" value={fmtDur(durMin)}            accent={P.sky}/>
            {selIsBike ? (
              <StatPill icon={<Gauge     className="w-4 h-4"/>} label="Avg Speed" value={fmtSpeed(avgSpeedMps)}   accent={P.violet}/>
            ) : (
              <StatPill icon={<Gauge     className="w-4 h-4"/>} label="Avg Pace" value={fmtPace(avgPace)}         accent={P.violet}/>
            )}
            <StatPill icon={<HeartPulse className="w-4 h-4"/>} label="Avg HR"   value={sel.avgHeartRate > 0 ? `${Math.round(sel.avgHeartRate)} bpm` : '—'} accent={P.rose}/>
            {altStats && (
              <>
                <StatPill icon={<TrendingUp   className="w-4 h-4"/>} label="Elev Gain" value={`+${altStats.gain} m`} accent={P.amber}/>
                <StatPill icon={<TrendingDown className="w-4 h-4"/>} label="Elev Loss" value={`−${altStats.loss} m`} accent={P.slate}/>
                <StatPill icon={<Mountain     className="w-4 h-4"/>} label="Max Alt"   value={`${altStats.max} m`}   accent={P.teal}/>
              </>
            )}
            {isFetching && (
              <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-card border border-border/50 text-xs text-muted-foreground">
                <Loader2 className="w-3.5 h-3.5 animate-spin"/>&nbsp;Fetching elevation…
              </div>
            )}
          </div>

          {/* Map + charts */}
          <div className="grid grid-cols-1 xl:grid-cols-5 gap-3 sm:gap-4">

            {/* Map — 3/5 */}
            <div className="xl:col-span-3 rounded-2xl border border-border/50 bg-card overflow-hidden shadow-[0_2px_12px_rgba(0,0,0,0.08)] dark:shadow-[0_2px_12px_rgba(0,0,0,0.3)]">
              <div className="px-5 pt-5 pb-3 border-b border-border/40 flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold tracking-tight">GPS Route</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {sel.startTime.toLocaleDateString(undefined, { weekday:'long', month:'long', day:'numeric', year:'numeric' })}
                  </p>
                </div>
                {altStats && alts.length > 0 && (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <span>{altStats.min}m</span>
                    <div className="w-20 h-2 rounded-full" style={{background:'linear-gradient(to right,#10b981,#f59e0b,#f43f5e)'}}/>
                    <span>{altStats.max}m</span>
                  </div>
                )}
              </div>
              <div className="relative h-[280px] sm:h-[420px]">
                {hasGps ? (
                  <MapPanel lats={gps.lats} lngs={gps.lngs} alts={alts} hoverIdx={hoverIdx} isDark={isDark}/>
                ) : (
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 p-6 text-center">
                    <Mountain className="w-10 h-10 text-muted-foreground/30"/>
                    <p className="text-sm font-medium text-muted-foreground">No GPS data</p>
                    <p className="text-xs text-muted-foreground/60">{selIsBike ? 'Indoor trainer ride.' : 'Indoor or treadmill run.'}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Charts — 2/5 */}
            <div className="xl:col-span-2 flex flex-col gap-4">

              {/* Elevation */}
              <div className="flex-1 rounded-2xl border border-border/50 bg-card overflow-hidden shadow-[0_2px_12px_rgba(0,0,0,0.06)] dark:shadow-[0_2px_12px_rgba(0,0,0,0.28)]">
                <div className="px-5 pt-4 pb-3 border-b border-border/40 flex items-start justify-between">
                  <div>
                    <p className="text-sm font-semibold tracking-tight">Elevation Profile</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {altStats
                        ? <>Hover to pin map · <span className="text-emerald-500 font-medium">+{altStats.gain}m</span> · <span className="text-rose-500 font-medium">−{altStats.loss}m</span></>
                        : isFetching ? 'Fetching SRTM terrain data…'
                        : hasGps     ? 'Waiting for elevation data'
                        : 'No GPS data'}
                    </p>
                  </div>
                  {fetchErr && !isFetching && (
                    <button onClick={retry}
                      className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors shrink-0 mt-0.5">
                      <RefreshCw className="w-3 h-3"/>Retry
                    </button>
                  )}
                </div>
                <div className="p-3">
                  {isFetching ? (
                    <div className="flex flex-col items-center justify-center h-40 gap-3">
                      <Loader2 className="w-5 h-5 animate-spin" style={{ color: P.emerald }}/>
                      <span className="text-xs text-muted-foreground">Loading terrain elevation…</span>
                    </div>
                  ) : elevOpt ? (
                    <ReactECharts option={elevOpt} style={{ height: 180 }}
                      onEvents={{ mousemove: onElevHover, mouseout: () => setHoverIdx(null) }}/>
                  ) : (
                    <div className="flex flex-col items-center justify-center h-40 gap-2 text-center">
                      <span className="text-xs text-muted-foreground">
                        {fetchErr ?? (hasGps ? 'No elevation data for this activity.' : 'No GPS — cannot derive elevation.')}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* HR */}
              <div className="flex-1 rounded-2xl border border-border/50 bg-card overflow-hidden shadow-[0_2px_12px_rgba(0,0,0,0.06)] dark:shadow-[0_2px_12px_rgba(0,0,0,0.28)]">
                <div className="px-5 pt-4 pb-3 border-b border-border/40">
                  <p className="text-sm font-semibold tracking-tight">Heart Rate over Distance</p>
                  <p className="text-xs text-muted-foreground mt-0.5">LTHR 181 bpm · Zone 2 ceiling 161 bpm</p>
                </div>
                <div className="p-3">
                  {hrOpt
                    ? <ReactECharts option={hrOpt} style={{ height: 180 }}/>
                    : <div className="flex items-center justify-center h-40 text-xs text-muted-foreground">No HR data.</div>
                  }
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
